


import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Skeleton,
  TablePagination,
  Tooltip,
  Chip,
  Modal,
  Autocomplete,
  Button,
  Switch,
  FormControlLabel,
  Card,
  InputAdornment,
  Fade,
  Zoom,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import LocalPharmacyIcon from "@mui/icons-material/LocalPharmacy";
import CategoryIcon from "@mui/icons-material/Category";
import SellIcon from "@mui/icons-material/Sell";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import BlockIcon from "@mui/icons-material/Block";
import SearchIcon from "@mui/icons-material/Search";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import AddOTCProductModal from "./AddOTCProductModal";
import { toast } from "react-toastify";
import debounce from "lodash.debounce";

// DRUGS color palette and styles for unified UI
const PRIMARY_GRADIENT = "linear-gradient(135deg, #667eea 0%, #764ba2 100%)";
const PRIMARY_COLOR = "#667eea";
const SECONDARY_COLOR = "#764ba2";
const GREEN_COLOR = "#059669";
const RED_COLOR = "#dc2626";

// Same tag color logic as DrugsProduct
const getTagColor = (tag) => {
  const upper = tag.toUpperCase();
  const tagMap = {
    PWC: { border: RED_COLOR, text: RED_COLOR, bg: "#fef2f2" },
    DRUGS: { border: PRIMARY_COLOR, text: PRIMARY_COLOR, bg: "#eef2ff" },
    AYURVEDA: { border: GREEN_COLOR, text: GREEN_COLOR, bg: "#ecfdf5" },
    HOMEO: { border: SECONDARY_COLOR, text: SECONDARY_COLOR, bg: "#f3e8ff" },
    OTC: { border: "#ea580c", text: "#ea580c", bg: "#fff7ed" },
    PRESCRIPTION: { border: "#0891b2", text: "#0891b2", bg: "#ecfeff" },
    TABLET: { border: "#be123c", text: "#be123c", bg: "#fff1f2" },
    SYRUP: { border: "#a855f7", text: "#a855f7", bg: "#faf5ff" },
    INJECTION: { border: "#dc2626", text: "#dc2626", bg: "#fef2f2" },
    CAPSULE: { border: "#0d9488", text: "#0d9488", bg: "#f0fdfa" },
  };
  return (
    tagMap[upper] || {
      border: PRIMARY_COLOR,
      text: PRIMARY_COLOR,
      bg: "#eef2ff",
    }
  );
};

const StyledCard = styled(Card)({
  background: PRIMARY_GRADIENT,
  color: "white",
  borderRadius: 20,
  padding: 24,
  boxShadow: "0 20px 40px rgba(102,126,234,0.12)",
  position: "relative",
  overflow: "hidden",
  marginBottom: 32,
  "&::before": {
    content: '""',
    position: "absolute",
    top: -50,
    right: -50,
    width: 100,
    height: 100,
    background: "rgba(255,255,255,0.12)",
    borderRadius: "50%",
  },
});

const StyledTableContainer = styled(TableContainer)({
  borderRadius: 16,
  overflow: "auto",
  boxShadow: "0 20px 60px rgba(0,0,0,0.08)",
  border: "1px solid rgba(226,232,240,0.5)",
  background: "#fff",
  "&::-webkit-scrollbar": {
    height: 8,
    width: 8,
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: "#f1f1f1",
    borderRadius: 4,
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "#c1c1c1",
    borderRadius: 4,
    "&:hover": {
      backgroundColor: "#a8a8a8",
    },
  },
});

const StyledTableHead = styled(TableHead)({
  background: "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
  "& .MuiTableCell-head": {
    fontWeight: 700,
    fontSize: "0.92rem",
    color: "#374151",
    borderBottom: "2px solid #e5e7eb",
    padding: 16,
    whiteSpace: "nowrap",
    minWidth: 120,
  },
});

const StyledTableRow = styled(TableRow)({
  transition: "all 0.2s ease",
  "&:hover": {
    backgroundColor: "#f8fafc",
    transform: "translateX(4px)",
    boxShadow: "4px 0 12px rgba(59,130,246,0.08)",
  },
  "& .MuiTableCell-root": {
    borderBottom: "1px solid #f1f5f9",
    padding: 14,
    whiteSpace: "nowrap",
    minWidth: 120,
  },
});

const StyledTable = styled(Table)({
  minWidth: 1200,
  tableLayout: "auto",
});

const StyledModal = styled(Modal)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

const ModalContent = styled(Paper)({
  background: "linear-gradient(135deg, #fff 0%, #f8fafc 100%)",
  borderRadius: 24,
  padding: 32,
  minWidth: 400,
  maxWidth: "90vw",
  border: "1px solid #e2e8f0",
  boxShadow: "0 25px 80px rgba(0,0,0,0.09)",
});

const ActionButton = styled(Button)({
  borderRadius: 10,
  textTransform: "none",
  fontWeight: 600,
  padding: "8px 16px",
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 8px 20px rgba(59,130,246,0.12)",
  },
});

const BASE_URL = "https://api.medstown.com";

export default function OTCProduct({ type }) {
  const [products, setProducts] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [tagModalOpen, setTagModalOpen] = useState(false);
  const [activeProduct, setActiveProduct] = useState(null);
  const [tagInput, setTagInput] = useState([]);
  const [otcproductsModalOpen, setOtcProductsModalOpen] = useState(false);
  const [availableTags, setAvailableTags] = useState(
    ["drugs", "pwc", "ayurveda", "Homeo", "otc"].map((tag) => tag.toUpperCase())
  );
  const [priceModalOpen, setPriceModalOpen] = useState(false);
  const [editPrice, setEditPrice] = useState("");
  const newTags = tagInput.filter((t) => !availableTags.includes(t));
  if (newTags.length)
    setAvailableTags((prev) => [...new Set([...prev, ...newTags])]);
  const navigate = useNavigate();

  // --- Data fetch
  const fetchOTCProducts = async (pageNum = 0, limit = rowsPerPage) => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${BASE_URL}/api/medshubs/otc-products-list?page=${
          pageNum + 1
        }&limit=${limit}`
      );
      setProducts(res.data.data.list || []);
      setTotalCount(res.data.data.totalCount || 0);
    } catch {
      setProducts([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  // ✅ FIXED: searchOTCProducts now accepts page and limit parameters
  const searchOTCProducts = async (query, pageNum = 0, limit = rowsPerPage) => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${BASE_URL}/api/medshubs/otc-products/cache/search?search=${query}&page=${
          pageNum + 1
        }&limit=${limit}`
      );
      setProducts(res.data.data.list || []);
      setTotalCount(res.data.data.totalCount || 0);
    } catch {
      setProducts([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  // ✅ FIXED: debouncedSearch now passes page and rowsPerPage
  const debouncedSearch = useMemo(
    () =>
      debounce((value) => {
        if (value.trim()) {
          searchOTCProducts(value.trim(), page, rowsPerPage);
        } else {
          fetchOTCProducts(page, rowsPerPage);
        }
      }, 500),
    [page, rowsPerPage]
  );

  useEffect(() => {
    if (!search) fetchOTCProducts(page, rowsPerPage);
  }, [page, rowsPerPage]);

  useEffect(() => {
    debouncedSearch(search);
    return () => debouncedSearch.cancel();
  }, [search, debouncedSearch]);

  // ✅ FIXED: handlePageChange now triggers search if active
  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    if (search.trim()) {
      searchOTCProducts(search.trim(), newPage, rowsPerPage);
    }
  };

  // ✅ FIXED: handleRowsPerPageChange now triggers search if active
  const handleRowsPerPageChange = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    if (search.trim()) {
      searchOTCProducts(search.trim(), 0, newRowsPerPage);
    }
  };

  // Handle status toggle (Active/Inactive)
  const handleStatusToggle = async (item) => {
    setProducts((prev) =>
      prev.map((el) =>
        el._id === item._id ? { ...el, status: !el.status } : el
      )
    );

    try {
      const payload = {
        status: !item.status,
      };
      console.log(payload);

      const response = await axios.patch(
        `${BASE_URL}/api/medshubs/otcproducts/update/${item._id}`,
        payload
      );

      if (response.status === 200) {
        toast.success(
          !item.status
            ? "Product activated successfully!"
            : "Product deactivated successfully!"
        );
      } else {
        setProducts((prev) =>
          prev.map((el) =>
            el._id === item._id ? { ...el, status: item.status } : el
          )
        );
        toast.error("Status update failed");
      }
    } catch (error) {
      setProducts((prev) =>
        prev.map((el) =>
          el._id === item._id ? { ...el, status: item.status } : el
        )
      );
      console.error("Failed to update status:", error.response?.data || error);
      toast.error("Failed to update product status");
    }
  };

  // Stock toggle - using 'available'
  const handleStockToggle = async (item) => {
    const newAvailable = item.available === "1" ? "0" : "1";
    setProducts((prev) =>
      prev.map((el) =>
        el._id === item._id ? { ...el, available: newAvailable } : el
      )
    );
    try {
      const payload = { available: newAvailable };
      await axios.patch(
        `${BASE_URL}/api/medshubs/otcproducts/update/${item._id}`,
        payload
      );
      toast.success(
        newAvailable === "0" ? "Marked as Out of Stock" : "Now In Stock"
      );
    } catch {
      setProducts((prev) =>
        prev.map((el) =>
          el._id === item._id ? { ...el, available: item.available } : el
        )
      );
      toast.error("Failed to update stock status");
    }
  };

  const handleSaveTags = async () => {
    if (!activeProduct) return;
    const payload = {
      sku_id: activeProduct.sku_id,
      type: "non-prescriptions",
      tags: tagInput,
    };
    try {
      await axios.post(`${BASE_URL}/customer/tags/medshub/update`, payload);
      toast.success("Tags updated successfully");
      setTagModalOpen(false);
      if (search.trim()) {
        searchOTCProducts(search.trim(), page, rowsPerPage);
      } else {
        fetchOTCProducts(page, rowsPerPage);
      }
    } catch {
      toast.error("Failed to update tags.");
    }
  };

  // Delete product
  const handleDelete = async (id) => {
    console.log("Deleting product with ID:", id);
    if (!window.confirm("Are you sure you want to delete this product?"))
      return;

    try {
      const res = await axios.delete(
        `${BASE_URL}/api/medshubs/otc-products/${id}`
      );
      console.log(res.data);
      if (res.data.success) {
        alert("Product deleted successfully");
        if (search.trim()) {
          searchOTCProducts(search.trim(), page, rowsPerPage);
        } else {
          fetchOTCProducts(page, rowsPerPage);
        }
      } else {
        alert(res.data.message || "Delete failed");
      }
    } catch (err) {
      console.error(err);
      alert("Server error");
    }
  };

  return (
    <Box>
      {/* CARD AREA */}
      <StyledCard>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar
              sx={{
                bgcolor: "rgba(255,255,255,0.2)",
                width: 56,
                height: 56,
              }}
            >
              <LocalPharmacyIcon sx={{ fontSize: 28 }} />
            </Avatar>
            <Box>
              <Typography variant="h4" fontWeight={700}>
                {loading ? (
                  <Skeleton
                    variant="text"
                    width={100}
                    sx={{ bgcolor: "rgba(255,255,255,0.2)" }}
                  />
                ) : (
                  totalCount.toLocaleString()
                )}
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                Total OTC Products
              </Typography>
            </Box>
          </Box>
          <Button
            startIcon={<AddCircleIcon />}
            variant="contained"
            sx={{
              background: PRIMARY_GRADIENT,
              color: "#fff",
              borderRadius: 8,
              fontWeight: 700,
              "&:hover": { background: SECONDARY_COLOR },
            }}
            onClick={() => setOtcProductsModalOpen(true)}
          >
            Add New OTC Product
          </Button>
          <AddOTCProductModal
            open={otcproductsModalOpen}
            onClose={() => setOtcProductsModalOpen(false)}
          />
        </Box>
      </StyledCard>

      {/* SEARCH AREA */}
      <Box
        mb={3}
        p={3}
        borderRadius={16}
        sx={{
          background: "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
          border: "1px solid rgba(226, 232, 240, 0.8)",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
        }}
      >
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <SearchIcon sx={{ color: "#64748b", fontSize: 24 }} />
          <Typography variant="h6" fontWeight={600} color="#374151">
            Search OTC Products
          </Typography>
        </Box>

        {/* Search Bar */}
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search OTC products by name, pack size, or SKU..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#64748b" }} />
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 3,
              backgroundColor: "#ffffff",
              "& fieldset": {
                borderColor: "rgba(226, 232, 240, 0.8)",
              },
              "&:hover fieldset": {
                borderColor: "#3b82f6",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#3b82f6",
                borderWidth: "2px",
              },
            },
          }}
        />
      </Box>

      {/* Horizontal Scroll Notice */}
      <Box
        sx={{
          mb: 2,
          p: 1,
          backgroundColor: "#f0f9ff",
          borderRadius: 2,
          border: "1px solid #0ea5e9",
          display: { xs: "block", lg: "none" },
        }}
      >
        <Typography variant="caption" color="#0369a1" fontWeight={600}>
          💡 Scroll horizontally to view all columns
        </Typography>
      </Box>

      {/* TABLE WITH HORIZONTAL SCROLL */}
      <StyledTableContainer component={Paper}>
        <StyledTable>
          <StyledTableHead>
            <TableRow>
              <TableCell sx={{ minWidth: 200 }}>
                <Box display="flex" alignItems="center" gap={1}>
                  <LocalPharmacyIcon sx={{ fontSize: 18 }} />
                  Product Name
                </Box>
              </TableCell>
              <TableCell sx={{ minWidth: 120 }}>
                <Box display="flex" alignItems="center" gap={1}>
                  <CategoryIcon sx={{ fontSize: 18 }} />
                  Pack Size
                </Box>
              </TableCell>
              <TableCell sx={{ minWidth: 100 }}>
                <Box display="flex" alignItems="center" gap={1}>
                  <SellIcon sx={{ fontSize: 18 }} />
                  Price (₹)
                </Box>
              </TableCell>
              <TableCell sx={{ minWidth: 180 }}>Tags</TableCell>
              <TableCell sx={{ minWidth: 150 }}>Status</TableCell>
              <TableCell sx={{ minWidth: 150 }}>Stock</TableCell>
              <TableCell sx={{ minWidth: 140 }}>Actions</TableCell>
            </TableRow>
          </StyledTableHead>
          <TableBody>
            {loading ? (
              Array.from({ length: rowsPerPage }).map((_, i) => (
                <StyledTableRow key={i}>
                  <TableCell sx={{ minWidth: 200 }}>
                    <Skeleton width="80%" height={32} />
                  </TableCell>
                  <TableCell sx={{ minWidth: 120 }}>
                    <Skeleton width="50%" height={32} />
                  </TableCell>
                  <TableCell sx={{ minWidth: 100 }}>
                    <Skeleton width="40%" height={32} />
                  </TableCell>
                  <TableCell sx={{ minWidth: 180 }}>
                    <Skeleton width="75%" height={32} />
                  </TableCell>
                  <TableCell sx={{ minWidth: 150 }}>
                    <Skeleton width="100%" height={32} />
                  </TableCell>
                  <TableCell sx={{ minWidth: 150 }}>
                    <Skeleton width="100%" height={32} />
                  </TableCell>
                  <TableCell sx={{ minWidth: 140 }}>
                    <Skeleton width="30%" height={32} />
                  </TableCell>
                </StyledTableRow>
              ))
            ) : products?.length > 0 ? (
              products.map((item, idx) => (
                <Fade in={true} timeout={300 + idx * 40} key={item._id}>
                  <StyledTableRow>
                    <TableCell sx={{ minWidth: 200 }}>
                      <Tooltip title={item.name || "NA"}>
                        <Typography
                          onClick={() =>
                            navigate(
                              `/admin/otc-product-individual-page/${item?.sku_id}`
                            )
                          }
                          sx={{
                            maxWidth: 180,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            cursor: "pointer",
                            color: "primary.main",
                            fontWeight: 600,
                            "&:hover": {
                              textDecoration: "underline",
                              color: PRIMARY_COLOR,
                            },
                          }}
                        >
                          {item.name || "NA"}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ minWidth: 120 }}>
                      <Chip
                        label={item.pack_size_label || "NA"}
                        size="small"
                        variant="outlined"
                        sx={{ borderRadius: 2 }}
                      />
                    </TableCell>
                    <TableCell sx={{ minWidth: 100 }}>
                      <Box sx={{ display: "flex" }}>
                        <Typography
                          variant="body2"
                          fontWeight={700}
                          color={GREEN_COLOR}
                        >
                          ₹{Number(item.price) || 0}
                        </Typography>
                        <Button
                          onClick={() => {
                            setActiveProduct(item);
                            setEditPrice(item.price || "");
                            setPriceModalOpen(true);
                          }}
                        >
                          <EditIcon size={18} />
                        </Button>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ minWidth: 180 }}>
                      <Box display="flex" flexWrap="wrap" gap={0.5}>
                        {item.tags?.length > 0 ? (
                          item.tags.slice(0, 2).map((tag, tagIndex) => {
                            const color = getTagColor(tag);
                            return (
                              <Chip
                                key={tagIndex}
                                label={tag.toUpperCase()}
                                size="small"
                                variant="outlined"
                                sx={{
                                  borderRadius: 2,
                                  fontSize: "0.7rem",
                                  borderColor: color.border,
                                  color: color.text,
                                  backgroundColor: color.bg,
                                }}
                              />
                            );
                          })
                        ) : (
                          <Chip
                            label="No Tags"
                            size="small"
                            variant="outlined"
                            sx={{
                              borderRadius: 2,
                              borderColor: "#e5e7eb",
                              color: "#6b7280",
                              backgroundColor: "#f9fafb",
                            }}
                          />
                        )}
                        {item.tags?.length > 2 && (
                          <Chip
                            label={`+${item.tags.length - 2}`}
                            size="small"
                            variant="outlined"
                            sx={{ borderRadius: 2, fontSize: "0.7rem" }}
                          />
                        )}
                      </Box>
                    </TableCell>

                    {/* Status Column with Switch */}
                    <TableCell sx={{ minWidth: 150 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={item.status || false}
                            onChange={() => handleStatusToggle(item)}
                            size="small"
                            sx={{
                              "& .MuiSwitch-switchBase.Mui-checked": {
                                color: GREEN_COLOR,
                              },
                              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                                {
                                  backgroundColor: GREEN_COLOR,
                                },
                            }}
                          />
                        }
                        label={
                          <Box display="flex" alignItems="center" gap={0.5}>
                            {item.status ? (
                              <CheckCircleIcon
                                sx={{ fontSize: 14, color: GREEN_COLOR }}
                              />
                            ) : (
                              <BlockIcon
                                sx={{ fontSize: 14, color: RED_COLOR }}
                              />
                            )}
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: "0.7rem",
                                color: item.status ? GREEN_COLOR : RED_COLOR,
                                fontWeight: 600,
                              }}
                            >
                              {item.status ? "Active" : "Inactive"}
                            </Typography>
                          </Box>
                        }
                        sx={{ ml: 0 }}
                      />
                    </TableCell>

                    {/* Stock Column with CORRECTED LOGIC */}
                    <TableCell sx={{ minWidth: 150 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={item.available === "1"}
                            onChange={() => handleStockToggle(item)}
                            size="small"
                            sx={{
                              "& .MuiSwitch-switchBase.Mui-checked": {
                                color: GREEN_COLOR,
                              },
                              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                                {
                                  backgroundColor: GREEN_COLOR,
                                },
                              "& .MuiSwitch-switchBase": {
                                color: RED_COLOR,
                              },
                              "& .MuiSwitch-track": {
                                backgroundColor: RED_COLOR,
                              },
                            }}
                          />
                        }
                        label={
                          <Box display="flex" alignItems="center" gap={0.5}>
                            {item.available === "0" ? (
                              <BlockIcon
                                sx={{ fontSize: 14, color: RED_COLOR }}
                              />
                            ) : (
                              <CheckCircleIcon
                                sx={{ fontSize: 14, color: GREEN_COLOR }}
                              />
                            )}
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: "0.7rem",
                                color:
                                  item.available === "0"
                                    ? RED_COLOR
                                    : GREEN_COLOR,
                                fontWeight: 600,
                              }}
                            >
                              {item.available === "0"
                                ? "Out of Stock"
                                : "In Stock"}
                            </Typography>
                          </Box>
                        }
                        sx={{ ml: 0 }}
                      />
                    </TableCell>

                    <TableCell sx={{ minWidth: 140 }}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                        }}
                      >
                        <ActionButton
                          size="small"
                          variant="contained"
                          startIcon={<EditIcon />}
                          onClick={() => {
                            setActiveProduct(item);
                            setTagInput(item.tags || []);
                            setTagModalOpen(true);
                          }}
                          sx={{
                            background: PRIMARY_GRADIENT,
                            borderRadius: 2,
                            fontSize: "0.75rem",
                            minWidth: 110,
                            color: "#fff",
                          }}
                        >
                          Edit Tags
                        </ActionButton>
                        <ActionButton
                          size="small"
                          variant="contained"
                          startIcon={<DeleteIcon />}
                          onClick={() => handleDelete(item._id)}
                          sx={{
                            background: "red",
                            borderRadius: 2,
                            fontSize: "0.75rem",
                            minWidth: 110,
                            color: "#fff",
                          }}
                        >
                          Delete
                        </ActionButton>
                      </Box>
                    </TableCell>
                  </StyledTableRow>
                </Fade>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Box py={4}>
                    <LocalPharmacyIcon
                      sx={{ fontSize: 48, color: "#cbd5e1", mb: 2 }}
                    />
                    <Typography variant="h6" color={PRIMARY_COLOR}>
                      No OTC Products Found
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </StyledTable>
        {/* ✅ FIXED: Show pagination always when there are products OR when not searching */}
        {(products?.length > 0 || !search) && (
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={handlePageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[10, 20, 50]}
            sx={{
              borderTop: "1px solid #f1f5f9",
              "& .MuiTablePagination-toolbar": {
                backgroundColor: "#f8fafc",
              },
            }}
          />
        )}
      </StyledTableContainer>

      {/* TAG EDIT MODAL */}
      <StyledModal open={tagModalOpen} onClose={() => setTagModalOpen(false)}>
        <Zoom in={tagModalOpen}>
          <ModalContent>
            <Box display="flex" alignItems="center" gap={2} mb={3}>
              <Avatar sx={{ bgcolor: PRIMARY_COLOR }}>
                <EditIcon />
              </Avatar>
              <Box>
                <Typography variant="h5" fontWeight={700}>
                  Edit Tags
                </Typography>
                <Typography variant="body2" color="#64748b">
                  {activeProduct?.name}
                </Typography>
              </Box>
            </Box>
            <Autocomplete
              multiple
              freeSolo
              options={availableTags}
              value={tagInput}
              onChange={(e, newValue) => setTagInput(newValue)}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const color = getTagColor(option);
                  const isNew = !availableTags.includes(option);
                  return (
                    <Chip
                      key={option}
                      label={option + (isNew ? " (new)" : "")}
                      variant="outlined"
                      sx={{
                        borderRadius: 2,
                        m: 0.25,
                        borderColor: isNew ? GREEN_COLOR : color.border,
                        color: isNew ? GREEN_COLOR : color.text,
                        backgroundColor: isNew ? "#ecfdf5" : color.bg,
                      }}
                      {...getTagProps({ index })}
                    />
                  );
                })
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Tags"
                  placeholder="Add tags..."
                  variant="outlined"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 3,
                    },
                  }}
                />
              )}
            />
            <Box mt={4} display="flex" justifyContent="flex-end" gap={2}>
              <ActionButton
                variant="outlined"
                onClick={() => setTagModalOpen(false)}
                sx={{ borderRadius: 2 }}
              >
                Cancel
              </ActionButton>
              <ActionButton
                variant="contained"
                onClick={handleSaveTags}
                sx={{
                  background: PRIMARY_GRADIENT,
                  borderRadius: 2,
                  color: "#fff",
                }}
              >
                Save
              </ActionButton>
            </Box>
          </ModalContent>
        </Zoom>
      </StyledModal>

      {/* Price edit */}
      <StyledModal
        open={priceModalOpen}
        onClose={() => setPriceModalOpen(false)}
      >
        <Zoom in={priceModalOpen}>
          <ModalContent>
            <Box display="flex" alignItems="center" gap={2} mb={3}>
              <Avatar sx={{ bgcolor: "#f59e0b" }}>
                <SellIcon />
              </Avatar>
              <Box>
                <Typography variant="h5" fontWeight={700}>
                  Edit Price
                </Typography>
                <Typography variant="body2" color="#64748b">
                  {activeProduct?.name}
                </Typography>
              </Box>
            </Box>

            <TextField
              fullWidth
              label="Price (₹)"
              type="number"
              value={editPrice}
              onChange={(e) => setEditPrice(e.target.value)}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 3,
                },
              }}
            />

            <Box mt={4} display="flex" justifyContent="flex-end" gap={2}>
              <ActionButton
                variant="outlined"
                onClick={() => setPriceModalOpen(false)}
                sx={{ borderRadius: 2 }}
              >
                Cancel
              </ActionButton>
              <ActionButton
                variant="contained"
                onClick={async () => {
                  try {
                    const type = "otc";
                    const payload = {
                      price: editPrice,
                    };

                    const response = await axios.put(
                      `${BASE_URL}/api/medshubs/update-price/${type}/${activeProduct?.sku_id}`,
                      payload
                    );

                    if (response.status === 200) {
                      toast.success("Price updated successfully!");
                      if (search.trim()) {
                        searchOTCProducts(search.trim(), page, rowsPerPage);
                      } else {
                        fetchOTCProducts(page, rowsPerPage);
                      }
                      setPriceModalOpen(false);
                    } else {
                      toast.error("Failed to update price");
                    }
                  } catch (error) {
                    console.error("Failed to update price:", error);
                    toast.error("Failed to update price");
                  }
                }}
                sx={{
                  background:
                    "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                  borderRadius: 2,
                }}
              >
                Save Changes
              </ActionButton>
            </Box>
          </ModalContent>
        </Zoom>
      </StyledModal>
    </Box>
  );
}